import { useThrottledCallback } from '@tanstack/react-pacer';
import { useCallback, useEffect, useState } from 'react';

import { useViewerStore } from '../../../store';
import { BottomNav, TopNav } from '../Navbar';
import { StyledContent, StyledWrapper } from './AnimatedNavBar.styles';

export type AnimatedNavBarProps = {
  children: (props: { documentClickHandler: () => void }) => React.ReactNode;
};

const THROTTLE_MOUSEMOVE_IN_MS = 250;

export function AnimatedNavBar({ children }: AnimatedNavBarProps) {
  const [visible, setVisible] = useState(true);
  const isSinglePageMode = useViewerStore((state) => state.pageMode === 'single');

  function documentClickHandler() {
    if (!isSinglePageMode) return;

    setVisible(false);
  }

  const throttledMousemoveHandler = useThrottledCallback(
    (event: MouseEvent) => {
      const clientY = event.clientY;
      const windowHeight = window.innerHeight;
      const threshold = 100;
      const isNearTop = clientY < threshold;
      const isNearBottom = windowHeight - clientY < threshold;

      if (isNearTop || isNearBottom) {
        setVisible(true);
      }
    },
    {
      wait: THROTTLE_MOUSEMOVE_IN_MS,
    }
  );

  const handleMouseLeave = useCallback((_event: MouseEvent) => {
    const DELAY_TO_HIDE = 200;

    setTimeout(() => {
      setVisible(false);
    }, DELAY_TO_HIDE);
  }, []);

  useEffect(() => {
    const { documentElement } = document;

    documentElement.addEventListener('mousemove', throttledMousemoveHandler);
    documentElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      documentElement.removeEventListener('mousemove', throttledMousemoveHandler);

      documentElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave, throttledMousemoveHandler]);

  return (
    <StyledWrapper>
      <TopNav visible={isSinglePageMode ? visible : true} />

      <StyledContent>{children({ documentClickHandler })}</StyledContent>

      <BottomNav visible={isSinglePageMode ? visible : true} />
    </StyledWrapper>
  );
}
